import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/services/api";

export interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type: string;
  priority: "low" | "normal" | "high" | "critical";
  read: boolean;
  readAt?: string;
  followUpAt?: string;
  sentBy?: {
    id: string;
    name: string;
  };
  escalatedFrom?: {
    id: string;
  } | null;
  createdAt: string;
}

export interface NotificationStats {
  total_unread: number;
  critical_unread: number;
  pending_escalation: number;
}

export const useNotifications = (perPage = 20, read?: boolean) => {
  return useQuery({
    queryKey: ["notifications", { perPage, read }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("per_page", String(perPage));
      if (read !== undefined) {
        params.set("read", String(read));
      }
      const response = await api.get(`/v1/notifications-management?${params.toString()}`);
      return response.data as { data: NotificationItem[] };
    },
    refetchInterval: 30000,
  });
};

export const useNotificationStats = () => {
  return useQuery({
    queryKey: ["notificationStats"],
    queryFn: async () => {
      const response = await api.get("/v1/notifications-management/stats");
      return response.data as NotificationStats;
    },
    refetchInterval: 15000,
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.patch(`/v1/notifications-management/${notificationId}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notificationStats"] });
    },
  });
};

export const useDismissCritical = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.post("/v1/notifications-management/dismiss-critical");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notificationStats"] });
    },
  });
};

export const useEscalationStatus = (notificationId: string) => {
  return useQuery({
    queryKey: ["escalationStatus", notificationId],
    queryFn: async () => {
      const response = await api.get(`/v1/notifications-management/${notificationId}/escalation-status`);
      return response.data;
    },
    enabled: !!notificationId,
  });
};
